import React, { useState } from 'react';
import { apiClient } from '../../../lib/apiClient.js';

function NoticeCard({ title, subtitle, legal, audience, timing, action, onGenerate }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function handle() {
    setBusy(true);
    setErr(null);
    try {
      await onGenerate();
    } catch (e) {
      setErr(e.message || 'Generation failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card overflow-hidden">
      <div className="px-5 py-4 border-b border-border bg-bg-s2 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-[14px] font-semibold text-text">{title}</h2>
          <p className="text-[12px] text-text-muted mt-0.5">{subtitle}</p>
        </div>
        <span className="flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-warning/10 text-warning border border-warning/20 whitespace-nowrap">
          Required
        </span>
      </div>
      <div className="p-5 space-y-4">
        <div className="grid grid-cols-3 gap-4 text-[12px]">
          <div>
            <p className="font-mono uppercase tracking-widest text-text-faint text-[10px] mb-0.5">Authority</p>
            <p className="text-text">{legal}</p>
          </div>
          <div>
            <p className="font-mono uppercase tracking-widest text-text-faint text-[10px] mb-0.5">Recipients</p>
            <p className="text-text">{audience}</p>
          </div>
          <div>
            <p className="font-mono uppercase tracking-widest text-text-faint text-[10px] mb-0.5">Timing</p>
            <p className="text-text">{timing}</p>
          </div>
        </div>
        {err && (
          <div className="rounded-md px-3 py-2 text-[12px] bg-danger/10 text-danger border border-danger/30">{err}</div>
        )}
        <button
          type="button"
          onClick={handle}
          disabled={busy}
          className="w-full rounded-md bg-accent px-4 py-2 text-sm font-semibold text-white hover:bg-accent-dark transition-colors disabled:opacity-50"
        >
          {busy ? 'Generating…' : action}
        </button>
      </div>
    </div>
  );
}

export default function Notices() {
  return (
    <div className="max-w-2xl mx-auto space-y-4">
      {/* Header */}
      <div className="card overflow-hidden flex">
        <div className="w-1 bg-accent flex-shrink-0" />
        <div className="p-5 flex-1">
          <h1 className="text-[18px] font-bold text-text leading-tight">Mass Notices</h1>
          <p className="text-sm text-text-muted mt-1">
            Generate required ERISA participant notices. Each notice downloads as a text file
            ready for distribution or mail-merge.
          </p>
        </div>
      </div>

      <div className="card p-3.5 border-l-4 border-accent bg-accent/5">
        <p className="text-[12px] text-text font-medium">
          Notices are pre-populated with your plan's current data. Review before distributing.
          Retain a copy of each notice on file for 6 years (ERISA § 107).
        </p>
      </div>

      <NoticeCard
        title="Annual Safe Harbor Notice"
        subtitle="Describes the plan's safe harbor matching contribution and participant rights."
        legal="IRC § 401(k)(12)(D) · Treas. Reg. § 1.401(k)-3"
        audience="All eligible employees"
        timing="At least 30 days before plan year start"
        action="Generate Safe Harbor Notice"
        onGenerate={() => apiClient.downloadNotice('safe-harbor')}
      />

      <NoticeCard
        title="Special Tax Notice (402(f))"
        subtitle="Required before any eligible rollover distribution. Explains rollover options and withholding."
        legal="IRC § 402(f) · Treas. Reg. § 1.402(f)-1"
        audience="Any participant receiving an eligible rollover distribution"
        timing="No less than 30 days and no more than 180 days before distribution"
        action="Generate 402(f) Tax Notice"
        onGenerate={() => apiClient.downloadNotice('402f')}
      />

      {/* Coming soon */}
      <div className="card overflow-hidden opacity-60">
        <div className="px-5 py-4 border-b border-border bg-bg-s2 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-[14px] font-semibold text-text">Blackout Notice</h2>
            <p className="text-[12px] text-text-muted mt-0.5">30-day advance notice when restricting participant transactions.</p>
          </div>
          <span className="flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-bg-s3 text-text-faint border border-border whitespace-nowrap">
            Use Blackout Manager
          </span>
        </div>
        <div className="p-5">
          <p className="text-[12px] text-text-faint">
            Blackout notices are managed through the <strong>Blackout Manager</strong> page,
            which handles scheduling and the 30-day notice requirement (ERISA § 101(i)).
          </p>
        </div>
      </div>

      <p className="text-[11px] text-text-faint text-center font-mono pb-2">
        All notices are for demonstration purposes. Consult ERISA counsel before distribution.
      </p>
    </div>
  );
}
